import { useState } from "react";
import { toast } from "sonner";
import { Shield, User as UserIcon, Info, Loader2, Upload, ImageIcon } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Settings() {
  const { user, isAdmin } = useAuth();
  const { project, refresh } = useProject();

  const [name, setName] = useState(project.name);
  const [savingName, setSavingName] = useState(false);
  const [uploading, setUploading] = useState(false);

  const saveName = async () => {
    if (!name.trim()) { toast.error("Proje adı boş olamaz"); return; }
    setSavingName(true);
    try {
      await api.put("/settings", { project_name: name.trim() });
      await refresh();
      toast.success("Proje adı güncellendi");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSavingName(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/project-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      toast.success("Proje görseli güncellendi");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="mx-auto max-w-2xl animate-fade-up">
      <PageHeader overline="Hesap" title="Ayarlar" subtitle="Hesap bilgileriniz ve proje detayları" />

      <div className="space-y-4">
        {/* Project settings (admin) */}
        <div className="rounded-md border border-border bg-card p-6" data-testid="settings-project">
          <h2 className="mb-4 font-heading text-base font-bold">Proje</h2>

          <div className="mb-5 overflow-hidden rounded-md border border-border">
            <div className="relative">
              <img src={project.imageUrl} alt={project.name} className="h-40 w-full object-cover" data-testid="settings-project-image" />
              <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-center p-5 text-primary-foreground">
                <p className="overline text-white/60">Proje</p>
                <span className="font-heading text-3xl font-bold tracking-tight">{project.name}</span>
              </div>
            </div>
          </div>

          {isAdmin ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Proje Adı</Label>
                <div className="flex gap-2">
                  <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="project-name-input" />
                  <Button onClick={saveName} disabled={savingName} data-testid="save-project-name-button">
                    {savingName ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Proje Görseli</Label>
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border bg-secondary/40 px-4 py-5 text-sm text-muted-foreground transition-colors hover:bg-secondary" data-testid="project-image-label">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {uploading ? "Yükleniyor..." : "Yeni görsel yüklemek için tıklayın"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploading} data-testid="project-image-input" />
                </label>
              </div>
            </div>
          ) : (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <ImageIcon className="h-4 w-4" /> Proje adı ve görseli yalnızca yöneticiler tarafından değiştirilebilir.
            </p>
          )}
        </div>

        {/* Profile */}
        <div className="rounded-md border border-border bg-card p-6" data-testid="settings-profile">
          <h2 className="mb-4 font-heading text-base font-bold">Profil</h2>
          <div className="divide-y divide-border text-sm">
            <div className="flex justify-between py-3"><span className="text-muted-foreground">Ad Soyad</span><span className="font-medium">{user?.name}</span></div>
            <div className="flex justify-between py-3"><span className="text-muted-foreground">E-posta</span><span className="font-medium">{user?.email}</span></div>
            <div className="flex items-center justify-between py-3">
              <span className="text-muted-foreground">Rol</span>
              <Badge variant={user?.role === "admin" ? "default" : "secondary"} className="gap-1">
                {user?.role === "admin" ? <Shield className="h-3 w-3" /> : <UserIcon className="h-3 w-3" />}
                {user?.role === "admin" ? "Yönetici" : "Kullanıcı"}
              </Badge>
            </div>
          </div>
        </div>

        <div className="rounded-md border border-border bg-card p-6" data-testid="settings-about">
          <h2 className="mb-4 font-heading text-base font-bold">Uygulama Hakkında</h2>
          <div className="flex items-start gap-3 rounded-md bg-secondary/50 p-4 text-sm text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium text-foreground">Şantiye Gider Takip · Horoz Group</p>
              <p className="mt-1">Şantiye giderlerini kaydetmek, takip etmek ve analiz etmek için geliştirilmiş güvenli bir uygulama. Tüm işlemler geçmişe kaydedilir, hiçbir veri kalıcı olarak silinmez.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
